"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getBankAccounts = exports.getAccountNodesById = exports.getAccountNodesTree = void 0;
const reselect_1 = require("reselect");
const immutable_1 = require("immutable");
const luxon_1 = require("luxon");
const accounts_1 = require("companion-app-components/flux/accounts");
const selectors_1 = require("data/accountBalances/selectors");
const types_1 = require("./types");
const isSeparateAccount = (account) => (account.isExcludedFromBudgets && account.isExcludedFromReports && account.type !== 'INVESTMENT') ||
    (account.isExcludedFromReports && account.type === 'INVESTMENT');
function getL1GroupingDataCompanion(account) {
    switch (account.type) {
        case 'BANK':
        case 'CASH':
        case 'CREDIT':
            return {
                id: 'BANKING',
                displayLabel: 'Banking',
                displayOrder: 1,
                svgIconId: 'IconBanking',
                routeTo: '/transactions',
                type: types_1.accountTypes.banking,
            };
        case 'INVESTMENT':
            return {
                id: 'INVESTMENT',
                displayLabel: 'Investments',
                displayOrder: 2,
                svgIconId: 'IconInvestment',
                routeTo: '/investments',
                type: types_1.accountTypes.investment,
            };
        case 'LOAN':
        case 'OTHER_ASSET':
        case 'ASSET':
        case 'OTHER_LIABILITY':
        case 'REAL_ESTATE':
        case 'VEHICLE':
            return {
                id: 'ASSETS_LIABILITIES',
                displayLabel: 'Property & Debt',
                displayOrder: 3,
                svgIconId: 'IconLoan',
                routeTo: '/transactions',
                type: types_1.accountTypes.liabilities,
            };
        case 'GOAL':
            return {
                id: 'GOALS',
                displayLabel: 'Savings Goals',
                displayOrder: 4,
                svgIconId: 'IconLoan',
                routeTo: '/goals',
                type: types_1.accountTypes.goals,
            };
        default:
            return {
                id: 'OTHER',
                displayLabel: 'Other',
                displayOrder: 5,
                svgIconId: 'IconBanking',
                routeTo: '/transactions',
                type: types_1.accountTypes.other,
            };
    }
}
function getL1GroupingData(account) {
    if (isSeparateAccount(account)) {
        return {
            id: 'SEPARATE',
            displayLabel: 'Separate',
            displayOrder: 6,
            svgIconId: 'IconBanking',
            routeTo: account.type === types_1.accountTypes.investment ? '/investments' : '/transactions',
            type: types_1.accountTypes.separate,
        };
    }
    return getL1GroupingDataCompanion(account);
}
function hasL2Grouping(id, numberOfAccounts) {
    // log.log('Checking if node has L2 grouping', id, numberOfAccounts);
    switch (id) {
        case 'BANKING':
        case 'INVESTMENT':
        case 'ASSET':
        case 'LOAN':
        case 'PROPERTY':
        case 'ASSETS_LIABILITIES':
            return numberOfAccounts > 0;
        default:
            return false;
    }
}
function getL2GroupingDataCompanion(node, account) {
    switch (account.type) {
        case 'BANK':
        case 'CASH':
        case 'CREDIT':
            if (account.type === 'CASH' || account.subType === 'CHECKING') {
                const idCashChecking = isSeparateAccount(account) ? 'CASH_CHECKING_SEPARATE' : 'CASH_CHECKING';
                return {
                    id: idCashChecking,
                    displayLabel: 'Cash & Checking',
                    displayOrder: 1,
                    routeTo: node.routeTo,
                };
            }
            if (account.type === 'CREDIT') {
                const idCredit = isSeparateAccount(account) ? 'CREDIT_SEPARATE' : 'CREDIT';
                return {
                    id: idCredit,
                    displayLabel: 'Credit',
                    displayOrder: 2,
                    routeTo: node.routeTo,
                };
            }
            if (account.subType === 'SAVINGS' || account.subType === 'CD' || account.subType === 'MONEY_MARKET') {
                const idSavings = isSeparateAccount(account) ? 'SAVINGS_SEPARATE' : 'SAVINGS';
                return {
                    id: idSavings,
                    displayLabel: 'Savings',
                    displayOrder: 3,
                    routeTo: node.routeTo,
                };
            }
            return {
                id: 'OTHER_BANKING',
                displayLabel: 'Other Banking',
                displayOrder: 4,
                routeTo: node.routeTo,
            };
        case 'INVESTMENT':
            switch (account.subType) {
                case 'BROKERAGE':
                    return {
                        id: 'BROKERAGE',
                        displayLabel: 'Brokerage',
                        displayOrder: 1,
                        routeTo: node.routeTo,
                    };
                case 'IRA':
                case 'KEOGH':
                case 'SEP_IRA':
                case 'SIMPLE_IRA':
                case '401K':
                case '403B':
                case 'ROTH_IRA':
                case 'ROTH_401K':
                    return {
                        id: 'RETIREMENT',
                        displayLabel: 'Retirement',
                        displayOrder: 2,
                        routeTo: node.routeTo,
                    };
                case 'UGMA_UTMA':
                case '529':
                case 'OTHER_INVESTMENT':
                default:
                    return {
                        id: 'OTHER_INVESTMENT',
                        displayLabel: 'Other Investments',
                        displayOrder: 3,
                        routeTo: node.routeTo,
                    };
            }
        case 'OTHER_ASSET':
        case 'REAL_ESTATE':
        case 'VEHICLE':
            return {
                id: 'PROPERTY',
                displayLabel: 'Property',
                displayOrder: 1,
                routeTo: node.routeTo,
            };
        case 'LOAN':
        case 'OTHER_LIABILITY':
            return {
                id: 'DEBT',
                displayLabel: 'Debt',
                displayOrder: 1,
                routeTo: node.routeTo,
            };
        default:
            return {
                id: 'OTHER',
                displayLabel: 'Other',
                displayOrder: 1,
                routeTo: node.routeTo,
            };
    }
}
const investmentSubIds = ['BROKERAGE', 'RETIREMENT', 'OTHER_INVESTMENT'];
function getL2GroupingData(node, account) {
    if (account.isExcludedFromAccountBar) {
        return {
            id: 'HIDDEN',
            displayLabel: 'Hidden',
            displayOrder: 4,
            routeTo: account.type === types_1.accountTypes.investment ? '/investments' : node.routeTo,
            type: node.type,
        };
    }
    return getL2GroupingDataCompanion(node, account);
}
const findOrCreateNodeForGroup = (list, data, depth) => {
    let foundNode = list.find((node) => node.id === data.id);
    if (!foundNode) {
        list.push(foundNode = Object.assign(Object.assign({}, data), { depth, children: [] }));
    }
    return foundNode;
};
const accountIdsCache = {};
function memoizedAccountIdList(ids) {
    const normalizedIds = ids.sort((a, b) => a > b);
    const key = JSON.stringify(normalizedIds);
    if (!accountIdsCache[key]) {
        accountIdsCache[key] = (0, immutable_1.List)(ids);
    }
    return accountIdsCache[key];
}
const rollupAccountNodes = (node, balancesByAccountId, doIncludeGoalTxnsInBalances) => {
    var _a, _b, _c;
    let allAccountIds = [];
    let sumEndingAccountBalances = 0.0;
    let sumOnlineAccountBalances = 0.0;
    let sumCPOnlineAccountBalances = 0.0;
    let sumCPAvailableAccountBalances = 0.0;
    let sumCurrentAccountBalances = 0.0;
    let sumFinalAccountBalances = 0.0;
    let sumGoalsAccountBalances = 0.0;
    let earliestTransactionDate = null;
    let currency = null;
    if ((!doIncludeGoalTxnsInBalances || node.id !== 'GOALS') && node.children) {
        const isSeparateOrHiddenAcWithBankingAc = (node.id === types_1.accountTypes.separate || node.id === types_1.accountTypes.hidden) && ((_b = (_a = node.children) === null || _a === void 0 ? void 0 : _a.filter((child) => { var _a; return ((_a = child === null || child === void 0 ? void 0 : child.account) === null || _a === void 0 ? void 0 : _a.type) !== (types_1.accountTypes === null || types_1.accountTypes === void 0 ? void 0 : types_1.accountTypes.investment); })) === null || _b === void 0 ? void 0 : _b.length) > 0;
        node.children = node.children.map((child) => {
            var _a, _b;
            const skipTotalSumForSeparateOrHidden = isSeparateOrHiddenAcWithBankingAc && (((_a = child === null || child === void 0 ? void 0 : child.account) === null || _a === void 0 ? void 0 : _a.type) === types_1.accountTypes.investment || investmentSubIds.includes(child === null || child === void 0 ? void 0 : child.id));
            const childAfterRollup = rollupAccountNodes(child, balancesByAccountId, doIncludeGoalTxnsInBalances);
            // @ts-expect-error: Property 'toArray' does not exist on type 'never'.
            allAccountIds = allAccountIds.concat((_b = childAfterRollup.allAccountIds) === null || _b === void 0 ? void 0 : _b.toArray());
            if (!skipTotalSumForSeparateOrHidden) {
                sumEndingAccountBalances += childAfterRollup.sumEndingAccountBalances;
                sumCurrentAccountBalances += childAfterRollup.sumCurrentAccountBalances;
                sumOnlineAccountBalances += childAfterRollup.sumOnlineAccountBalances;
                sumCPOnlineAccountBalances += childAfterRollup.sumCPOnlineAccountBalances;
                sumCPAvailableAccountBalances += childAfterRollup.sumCPAvailableAccountBalances;
                sumFinalAccountBalances += childAfterRollup.sumFinalAccountBalances;
                sumGoalsAccountBalances += childAfterRollup.sumGoalsAccountBalances;
            }
            if (!earliestTransactionDate) {
                earliestTransactionDate = childAfterRollup.earliestTransactionDate;
            }
            else if (childAfterRollup.earliestTransactionDate &&
                luxon_1.DateTime.fromISO(childAfterRollup.earliestTransactionDate) < luxon_1.DateTime.fromISO(earliestTransactionDate)) {
                earliestTransactionDate = childAfterRollup.earliestTransactionDate;
            }
            if (!currency) {
                currency = childAfterRollup.currency;
            }
            else if (currency !== childAfterRollup.currency) {
                currency = 'N/A';
            }
            return childAfterRollup;
        });
        node.children = (0, immutable_1.List)(node.children);
    }
    if (node.account) {
        allAccountIds.push(node.account.id);
        const balancesForAccount = balancesByAccountId.get(node.account.id);
        if (balancesForAccount) {
            const currentAccountBalance = typeof balancesForAccount.currentBalance === 'number' ? balancesForAccount.currentBalance : 0.0;
            sumCurrentAccountBalances += currentAccountBalance;
            sumEndingAccountBalances += typeof balancesForAccount.endingBalance === 'number' ? balancesForAccount.endingBalance : 0.0;
            sumOnlineAccountBalances += typeof balancesForAccount.onlineBalance === 'number' ? balancesForAccount.onlineBalance : 0.0;
            sumCPAvailableAccountBalances += typeof balancesForAccount.cpAvailableBalance === 'number' ? balancesForAccount.cpAvailableBalance : 0.0;
            sumFinalAccountBalances += typeof balancesForAccount.finalBalance === 'number' ? balancesForAccount.finalBalance : 0.0;
            sumGoalsAccountBalances += typeof balancesForAccount.goalsBalance === 'number' ? balancesForAccount.goalsBalance : 0.0;
            const cpOnlineBalance = typeof balancesForAccount.cpOnlineBalance === 'number' ? balancesForAccount.cpOnlineBalance : 0.0;
            sumCPOnlineAccountBalances += (['BANK', 'CREDIT'].includes((_c = node.account) === null || _c === void 0 ? void 0 : _c.type) && !accounts_1.accountsUtils.isAccountManual(node.account)) ?
                cpOnlineBalance : currentAccountBalance;
            if (!earliestTransactionDate) {
                earliestTransactionDate = balancesForAccount.earliestTransactionDate;
            }
            else if (balancesForAccount.earliestTransactionDate &&
                luxon_1.DateTime.fromISO(balancesForAccount.earliestTransactionDate) < luxon_1.DateTime.fromISO(earliestTransactionDate)) {
                earliestTransactionDate = balancesForAccount.earliestTransactionDate;
            }
        }
        if (!currency) {
            currency = node.account.currency;
        }
        else if (currency !== node.account.currency) {
            currency = 'N/A';
        }
    }
    node.allAccountIds = memoizedAccountIdList(allAccountIds);
    node.sumEndingAccountBalances = sumEndingAccountBalances;
    node.sumOnlineAccountBalances = sumOnlineAccountBalances;
    node.sumCPOnlineAccountBalances = sumCPOnlineAccountBalances;
    node.sumCPAvailableAccountBalances = sumCPAvailableAccountBalances;
    node.sumCurrentAccountBalances = sumCurrentAccountBalances;
    node.sumFinalAccountBalances = sumFinalAccountBalances;
    node.sumGoalsAccountBalances = sumGoalsAccountBalances;
    node.currency = currency;
    node.earliestTransactionDate = earliestTransactionDate;
    node.hide = node.account ? node.account.isExcludedFromAccountBar : false;
    return new types_1.AccountNode(node);
};
function hasHiddenChild(node) {
    return node.children && node.children.find((x) => x.account.isExcludedFromAccountBar);
}
// @ts-expect-error: No overload matches this call.
exports.getAccountNodesTree = (0, reselect_1.createSelector)(accounts_1.accountsSelectors.getAccountsById, selectors_1.getAllBalancesByAccountId, (accountsById, balancesByAccountId, doShowGoalAccounts) => {
    const doIncludeGoalTxnsInBalances = false;
    // reduce all accounts into L1 groupings
    let l1Nodes = accountsById.reduce((list, account) => {
        if (doShowGoalAccounts || account.type !== 'GOAL') {
            const allNodes = findOrCreateNodeForGroup(list, {
                id: 'all',
                displayLabel: 'Total',
                routeTo: '/transactions',
                displayOrder: 0,
                expandable: false,
            }, 1);
            if (!isSeparateAccount(account)) {
                allNodes.children.push({
                    id: account.id || account.clientId,
                    depth: 2,
                    displayLabel: assert(account.name, 'name not defined'),
                    routeTo: '/transactions',
                    account,
                });
            }
            const l1Group = findOrCreateNodeForGroup(list, getL1GroupingData(account), 1);
            l1Group.children.push({
                id: account.id || account.clientId,
                depth: 2,
                displayLabel: assert(account.name, 'name not defined'),
                routeTo: account.type === types_1.accountTypes.investment ? '/investments' : '/transactions',
                account,
            });
            if (l1Group.id === types_1.accountTypes.separate) {
                if (account.type !== types_1.accountTypes.investment) {
                    l1Group.routeTo = '/transactions';
                }
            }
        }
        return list;
    }, []);
    // after creating all l1Nodes, sort by display order
    l1Nodes.sort((a, b) => a.displayOrder - b.displayOrder);
    // expand children in L1 groupings into L2 groupings (if required) and perform sorting
    l1Nodes.forEach((node) => {
        var _a, _b, _c, _d, _e, _f, _g;
        if (node.id !== 'all') {
            // @ts-expect-error: Property 'length' does not exist on type 'List<AccountNodeProps>'.
            if (hasL2Grouping(node.id, (_a = node.children) === null || _a === void 0 ? void 0 : _a.length) || hasHiddenChild(node)) {
                node.expandable = true;
                // @ts-expect-error: This expression is not callable.
                node.children = (_b = node.children) === null || _b === void 0 ? void 0 : _b.reduce((list, child) => {
                    var _a, _b, _c;
                    const l2Group = findOrCreateNodeForGroup(list, getL2GroupingData(node, child.account), 2);
                    l2Group.children.push({
                        id: ((_a = child.account) === null || _a === void 0 ? void 0 : _a.id) || ((_b = child.account) === null || _b === void 0 ? void 0 : _b.clientId),
                        depth: 3,
                        displayLabel: assert((_c = child.account) === null || _c === void 0 ? void 0 : _c.name, 'name not defined'),
                        routeTo: l2Group.routeTo,
                        account: child.account,
                    });
                    return list;
                }, []);
                (_c = node.children) === null || _c === void 0 ? void 0 : _c.sort((a, b) => a.displayOrder - b.displayOrder);
                (_d = node.children) === null || _d === void 0 ? void 0 : _d.forEach((l3Node) => {
                    var _a, _b;
                    // @ts-expect-error: Property 'length' does not exist on type 'List<AccountNodeProps>'.
                    l3Node.expandable = ((l3Node === null || l3Node === void 0 ? void 0 : l3Node.depth) === 2 && l3Node.id !== types_1.accountTypes.hidden) || ((_a = l3Node === null || l3Node === void 0 ? void 0 : l3Node.children) === null || _a === void 0 ? void 0 : _a.length) > 1;
                    (_b = l3Node.children) === null || _b === void 0 ? void 0 : _b.sort((a, b) => {
                        var _a;
                        return (_a = a.displayLabel) === null || _a === void 0 ? void 0 : _a.localeCompare(b.displayLabel, undefined, { numeric: true, sensitivity: 'base' });
                    });
                });
            }
            else {
                (_e = node.children) === null || _e === void 0 ? void 0 : _e.sort((a, b) => a.displayLabel.localeCompare(b.displayLabel, undefined, { numeric: true, sensitivity: 'base' }));
                (_f = node.children) === null || _f === void 0 ? void 0 : _f.forEach((l3Node) => {
                    var _a;
                    // @ts-expect-error: Type 'boolean' is not assignable to type 'number'.
                    l3Node.isLeaf = true;
                    // @ts-expect-error: Property 'length' does not exist on type 'List<AccountNodeProps>'.
                    l3Node.expandable = ((l3Node === null || l3Node === void 0 ? void 0 : l3Node.depth) === 2 && l3Node.id !== types_1.accountTypes.hidden) || ((_a = l3Node === null || l3Node === void 0 ? void 0 : l3Node.children) === null || _a === void 0 ? void 0 : _a.length) > 1;
                });
            }
        }
        else {
            // order net-worth children based on grouping data too
            (_g = node.children) === null || _g === void 0 ? void 0 : _g.sort((a, b) => {
                let compareVal = getL1GroupingData(a.account).displayOrder - getL1GroupingData(b.account).displayOrder;
                if (compareVal === 0) {
                    compareVal = getL2GroupingData(a, a.account).displayOrder - getL2GroupingData(b, b.account).displayOrder;
                }
                return compareVal;
            });
        }
    });
    l1Nodes = l1Nodes.map((node) => rollupAccountNodes(node, balancesByAccountId, doIncludeGoalTxnsInBalances));
    // instrument code for debug purpose to get user context
    const netWorth = l1Nodes.find((node) => node.id === 'all');
    const sumCurrentAccountBalances = l1Nodes
        .filter((node) => node.id !== 'all' && node.id !== 'SEPARATE')
        .reduce((total, node) => total + (typeof node.sumCurrentAccountBalances === 'number' ? node.sumCurrentAccountBalances : 0), 0);
    if (netWorth && netWorth.sumCurrentAccountBalances.toFixed(2) !== sumCurrentAccountBalances.toFixed(2)) {
        verify(false, 'Net Worth sumCurrentAccountBalances mismatch');
        // crashReporterInterface.reportError('NetWorthMismatch', (event) => event.addMetadata('nodes', l1Nodes));
    }
    const sumOnlineAccountBalances = l1Nodes
        .filter((node) => node.id !== 'all' && node.id !== 'SEPARATE')
        .reduce((total, node) => total + (typeof node.sumOnlineAccountBalances === 'number' ? node.sumOnlineAccountBalances : 0), 0);
    if (netWorth && netWorth.sumOnlineAccountBalances.toFixed(2) !== sumOnlineAccountBalances.toFixed(2)) {
        verify(false, 'Net Worth sumOnlineAccountBalances mismatch');
        // crashReporterInterface.reportError('NetWorthMismatch', (event) => event.addMetadata('nodes', l1Nodes));
    }
    return (0, immutable_1.List)(l1Nodes);
});
const getAccountsByIdForNode = (node) => {
    let flattenedList = [[node.id === types_1.accountTypes.hidden ? `${node.id}_${node.type}` : node.id, node]];
    if (node.children) {
        flattenedList = node.children.reduce((list, child) => (list.concat(getAccountsByIdForNode(child))), flattenedList);
    }
    return flattenedList;
};
exports.getAccountNodesById = (0, reselect_1.createSelector)(exports.getAccountNodesTree, (tree) => {
    if (!tree || tree.size === 0) {
        return (0, immutable_1.OrderedMap)();
    }
    return (0, immutable_1.OrderedMap)(tree.reduce((list, node) => (list.concat(getAccountsByIdForNode(node))), []));
});
exports.getBankAccounts = (0, reselect_1.createSelector)(exports.getAccountNodesTree, accounts_1.accountsSelectors.getAccountsById, (allNodes, allAccounts) => {
    const bankNodes = new Map();
    allNodes
        .forEach((node) => {
        node.allAccountIds
            .forEach((id) => {
            const childAcct = allAccounts.get(id);
            if ((childAcct === null || childAcct === void 0 ? void 0 : childAcct.type) === 'BANK' || (childAcct === null || childAcct === void 0 ? void 0 : childAcct.subType) === 'SAVINGS')
                bankNodes.set(id, childAcct);
        });
    });
    return (0, immutable_1.Map)(bankNodes);
});
